import React, { useEffect } from 'react'
import { Navigate, Route, Routes as RouterRoutes } from 'react-router'

import { YourClinic } from './pages/YourClinic'
import { Start } from './pages/Start'
import { Verify } from './pages/Verify'
import { AcceptTerms } from './pages/AcceptTerms'
import { ViewMessage } from './pages/ViewMessage/index'
import { ListMessages } from './pages/ListMessages'
import { TooManyLoginAttempts } from './pages/TooManyLoginAttempts'
import { ConfirmSignOut } from './pages/ConfirmSignOut'
import { Welcome } from './pages/Welcome'
import { ViewPreferences } from './pages/ViewPreferences'
import { EditPreferences } from './pages/EditPreferences'
import { TermsDeclined } from './pages/TermsDeclined'
import { UrlRouteLoader } from './components/UrlRouteLoader'
import { PatientConsent } from './pages/PatientConsent'
import { PrintUnsignedConsent } from './pages/PrintUnsignedConsent'
import { useRouteParam } from './browser/useRouteParam'
import { TERMS_OF_USE_URL, PRIVACY_POLICY_URL, CONTACT_US_URL } from './constants/externalUrls'

// Leaves the app for an external page (in an effect, since rendering can happen more than once)
const ExternalRedirect: React.FC<{ to: string }> = ({ to }) => {
  useEffect(() => {
    window.location.href = to
  }, [to])
  return null
}

// Keeps the current query string when redirecting within the app
const RedirectKeepingSearch: React.FC<{ to: string }> = ({ to }) => (
  <Navigate replace to={{ pathname: to, search: window.location.search }} />
)

// nav.care/<token> links from texts and emails
const UrlTokenRedirect: React.FC = () => (
  <RedirectKeepingSearch to={`/url-loader/${useRouteParam('urlToken')}`} />
)

export const Routes = () => {
  return (
    <RouterRoutes>
      <Route path="/homecare-instructions/:id" element={<ViewMessage />} />
      <Route path="/patient-education/:id" element={<ViewMessage />} />
      <Route path="/homecare-instructions" element={<ListMessages />} />
      <Route path="/preferences/edit" element={<EditPreferences />} />
      <Route path="/preferences" element={<ViewPreferences />} />
      {/* /clinic/ is already taken by health_tracker_client; hence /your-clinic/ */}
      <Route path="/your-clinic/:id" element={<YourClinic />} />
      <Route path="/confirm-sign-out" element={<ConfirmSignOut />} />

      <Route path="/verify/:urlToken" element={<Verify />} />
      <Route path="/verify" element={<Navigate replace to="/" />} />
      <Route path="/patient-consent/:id" element={<PatientConsent />} />
      <Route path="/accept-terms" element={<AcceptTerms />} />
      <Route path="/start/:urlToken" element={<Start />} />
      {/*
        /help is no longer linked from inside the app (links go straight to the contact page), but
        patient-facing copy on AcceptTerms shows "nav.care/help" as a typeable URL. Patients may still
        use it from that copy, which is an additional barrier to removal beyond external references.
        TODO: https://navigatingcancer.atlassian.net/browse/HT-8879
      */}
      <Route path="/help" element={<ExternalRedirect to={CONTACT_US_URL} />} />
      {/*
        /terms and /privacy are no longer linked from inside the app (links go straight to the
        external pages). They're kept in case something outside the app still references them;
        remove once Datadog logs confirm they're no longer hit.
        TODO: https://navigatingcancer.atlassian.net/browse/HT-8879
      */}
      <Route path="/terms" element={<ExternalRedirect to={TERMS_OF_USE_URL} />} />
      <Route path="/privacy" element={<ExternalRedirect to={PRIVACY_POLICY_URL} />} />
      <Route path="/terms-declined/:urlToken" element={<TermsDeclined />} />
      <Route path="/url-loader/:urlToken" element={<UrlRouteLoader />} />
      <Route path="/verification-error" element={<TooManyLoginAttempts />} />
      <Route path="/print-unsigned-consent/:locale" element={<PrintUnsignedConsent />} />
      <Route path="/start" element={<RedirectKeepingSearch to="/" />} />
      <Route path="/support" element={<RedirectKeepingSearch to="/help" />} />
      <Route path="/:urlToken" element={<UrlTokenRedirect />} />
      <Route path="/" element={<Welcome />} />
      <Route path="*" element={<RedirectKeepingSearch to="/" />} />
    </RouterRoutes>
  )
}
